import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  createOfferingApi
} from "../../../api/offering/offering.api";

import {
  getCategoriesApi
} from "../../../api/categories/categories.api";

import {
  OFFERING_TYPES
} from "../constants/offering.constants";

import {
  validateOffering
} from "../validation/offering.validation";

import "../styles/offering-form.css";


const OfferingCreatePage = () => {

  const navigate = useNavigate();


  /*
  |--------------------------------------------------------------------------
  | FORM
  |--------------------------------------------------------------------------
  */

  const [formData, setFormData] = useState({
    name: "",
    categoryId: "",
    type: "",
    shortDescription: "",
    description: "",
    displayOrder: 0
  });


  /*
  |--------------------------------------------------------------------------
  | CATEGORIES
  |--------------------------------------------------------------------------
  */

  const [categories, setCategories] = useState([]);

  const [categoriesLoading, setCategoriesLoading] =
    useState(true);

  const [categoriesError, setCategoriesError] =
    useState("");


  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  const [loading, setLoading] =
    useState(false);

  const [errors, setErrors] =
    useState({});

  const [submitError, setSubmitError] =
    useState("");


  /*
  |--------------------------------------------------------------------------
  | FETCH CATEGORIES
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    let mounted = true;


    const loadCategories = async () => {

      try {

        setCategoriesLoading(true);
        setCategoriesError("");


        const response =
          await getCategoriesApi({
            activeOnly: true
          });


        /*
        |----------------------------------------------------------------------
        | BACKEND RESPONSE
        |----------------------------------------------------------------------
        |
        | Expected:
        |
        | {
        |   success: true,
        |   data: [...]
        | }
        |
        | Axios response:
        |
        | response.data.data
        |
        */

        const payload =
          response?.data;


        const categoryList =
          Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload?.categories)
                ? payload.categories
                : [];


        if (mounted) {

          setCategories(
            categoryList
          );

        }

      } catch (error) {

        console.error(
          "Failed to fetch categories:",
          error
        );


        if (mounted) {

          setCategories([]);

          setCategoriesError(
            error?.response?.data?.message ||
            "Failed to load categories"
          );

        }

      } finally {

        if (mounted) {

          setCategoriesLoading(false);

        }

      }

    };


    loadCategories();


    return () => {
      mounted = false;
    };

  }, []);


  /*
  |--------------------------------------------------------------------------
  | CHANGE
  |--------------------------------------------------------------------------
  */

  const handleChange = (event) => {

    const {
      name,
      value
    } = event.target;


    setFormData((previous) => ({
      ...previous,
      [name]: value
    }));


    /*
    |----------------------------------------------------------------------
    | REMOVE FIELD ERROR
    |----------------------------------------------------------------------
    */

    setErrors((previous) => {

      if (!previous[name]) {
        return previous;
      }


      const next = {
        ...previous
      };


      delete next[name];


      return next;

    });


    setSubmitError("");

  };


  /*
  |--------------------------------------------------------------------------
  | SUBMIT
  |--------------------------------------------------------------------------
  */

  const handleSubmit = async (event) => {

    event.preventDefault();


    setSubmitError("");


    /*
    |----------------------------------------------------------------------
    | FRONTEND VALIDATION
    |----------------------------------------------------------------------
    */

    const validationErrors =
      validateOffering(formData);


    if (
      validationErrors &&
      Object.keys(validationErrors).length > 0
    ) {

      setErrors(
        validationErrors
      );

      return;

    }


    try {

      setLoading(true);


      /*
      |----------------------------------------------------------------------
      | PAYLOAD
      |----------------------------------------------------------------------
      */

      const payload = {

        name:
          formData.name.trim(),

        categoryId:
          formData.categoryId,

        type:
          formData.type,

        shortDescription:
          formData.shortDescription.trim()
            || undefined,

        description:
          formData.description.trim()
            || undefined,

        displayOrder:
          Number(formData.displayOrder) || 0

      };


      const response =
        await createOfferingApi(
          payload
        );


      /*
      |----------------------------------------------------------------------
      | SUCCESS
      |----------------------------------------------------------------------
      */

      const offering =
        response?.data?.data ||
        response?.data ||
        response;


      if (offering?.id) {

        navigate(
          `/offerings/${offering.id}`
        );

      } else {

        navigate(
          "/offerings"
        );

      }

    } catch (error) {

      console.error(
        "Create offering failed:",
        error
      );


      const backendErrors =
        error?.response?.data?.details;


      if (
        backendErrors &&
        typeof backendErrors === "object"
      ) {

        setErrors(
          backendErrors
        );

      } else {

        setSubmitError(
          error?.response?.data?.message ||
          "Failed to create offering"
        );

      }

    } finally {

      setLoading(false);

    }

  };


  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <div className="offering-form-page">

      {/* HEADER */}

      <div className="offering-form-header">

        <div>

          <h1>
            Create Offering
          </h1>

          <p>
            Create a new product, solution, or service offering.
          </p>

        </div>

      </div>


      {/* ERROR */}

      {submitError && (
        <div className="form-error-message">
          {submitError}
        </div>
      )}


      {/* FORM */}

      <form
        className="offering-form"
        onSubmit={handleSubmit}
      >


        {/* NAME */}

        <div className="form-group">

          <label htmlFor="name">
            Offering Name *
          </label>

          <input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter offering name"
            disabled={loading}
          />

          {errors.name && (
            <span className="field-error">
              {errors.name}
            </span>
          )}

        </div>


        {/* CATEGORY */}

        <div className="form-group">

          <label htmlFor="categoryId">
            Category *
          </label>


          <select
            id="categoryId"
            name="categoryId"
            value={formData.categoryId}
            onChange={handleChange}
            disabled={
              loading ||
              categoriesLoading
            }
          >

            <option value="">
              {categoriesLoading
                ? "Loading categories..."
                : "Select category"}
            </option>


            {categories.map((category) => (

              <option
                key={category.id}
                value={category.id}
              >
                {category.name}
              </option>

            ))}

          </select>


          {categoriesError && (
            <div className="field-error">

              {categoriesError}

              <button
                type="button"
                onClick={() => {
                  window.location.reload();
                }}
                className="retry-button"
              >
                Retry
              </button>

            </div>
          )}


          {!categoriesLoading &&
            !categoriesError &&
            categories.length === 0 && (
              <span className="field-error">
                No active categories found.
              </span>
            )}


          {errors.categoryId && (
            <span className="field-error">
              {errors.categoryId}
            </span>
          )}

        </div>


        {/* TYPE */}

        <div className="form-group">

          <label htmlFor="type">
            Offering Type *
          </label>

          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleChange}
            disabled={loading}
          >

            <option value="">
              Select type
            </option>


            <option
              value={OFFERING_TYPES.PRODUCT}
            >
              Product
            </option>


            <option
              value={OFFERING_TYPES.SERVICE}
            >
              Service
            </option>


            <option
              value={OFFERING_TYPES.SOLUTION}
            >
              Solution
            </option>

          </select>


          {errors.type && (
            <span className="field-error">
              {errors.type}
            </span>
          )}

        </div>


        {/* SHORT DESCRIPTION */}

        <div className="form-group">

          <div className="form-label-row">

            <label htmlFor="shortDescription">
              Short Description
            </label>

            <span>
              {formData.shortDescription.length}/500
            </span>

          </div>


          <textarea
            id="shortDescription"
            name="shortDescription"
            value={formData.shortDescription}
            onChange={handleChange}
            maxLength={500}
            rows={3}
            placeholder="Short description"
            disabled={loading}
          />


          {errors.shortDescription && (
            <span className="field-error">
              {errors.shortDescription}
            </span>
          )}

        </div>


        {/* DESCRIPTION */}

        <div className="form-group">

          <label htmlFor="description">
            Description
          </label>

          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={6}
            placeholder="Enter detailed description"
            disabled={loading}
          />


          {errors.description && (
            <span className="field-error">
              {errors.description}
            </span>
          )}

        </div>


        {/* DISPLAY ORDER */}

        <div className="form-group">

          <label htmlFor="displayOrder">
            Display Order
          </label>

          <input
            id="displayOrder"
            name="displayOrder"
            type="number"
            min="0"
            step="1"
            value={formData.displayOrder}
            onChange={handleChange}
            disabled={loading}
          />


          {errors.displayOrder && (
            <span className="field-error">
              {errors.displayOrder}
            </span>
          )}

        </div>


        {/* ACTIONS */}

        <div className="offering-form-actions">

          <button
            type="button"
            className="button button-secondary"
            onClick={() =>
              navigate("/offerings")
            }
            disabled={loading}
          >
            Cancel
          </button>


          <button
            type="submit"
            className="button button-primary"
            disabled={
              loading ||
              categoriesLoading ||
              categories.length === 0
            }
          >

            {loading
              ? "Creating..."
              : "Create Offering"}

          </button>

        </div>

      </form>

    </div>
  );

};


export default OfferingCreatePage;